/**
 * The Certification register (piece 34, Certifications): a view across every
 * employee over the existing `RequirementCompletion` rows, not a table of its
 * own. It lists what lapses inside the lead time `hr.certificationExpiryLeadDays`,
 * soonest first, and the coverage `RequiredTraining` asks for and nobody holds.
 * 🔨 The arithmetic is here, pure, so the page and the daily run cannot
 * disagree about which certification is expiring or which training is missing.
 */
import { DAY_MS } from '../zonedTime';

/** The register's own address, and the routes the browser asks for, relative to the API root. */
export const HR_CERTIFICATIONS_ROUTE = '/hr/certifications';
export const HR_CERTIFICATIONS_PATH = '/hr/certifications';
export const HR_REQUIRED_TRAINING_PATH = '/hr/required-training';
export function requiredTrainingDeactivatePath(id: string): string {
  return `${HR_REQUIRED_TRAINING_PATH}/${encodeURIComponent(id)}/deactivate`;
}

/** The two checklist kinds the daily run raises (piece 34, Certifications). */
export const CERTIFICATION_EXPIRING_KIND = 'CertificationExpiring';
export const REQUIRED_TRAINING_MISSING_KIND = 'RequiredTrainingMissing';
/** An expiring item is sourced on the completion that lapses; a missing one on the requirement nobody holds. */
export const CERTIFICATION_EXPIRING_SOURCE = 'RequirementCompletion';
export const REQUIRED_TRAINING_MISSING_SOURCE = 'RequirementType';
/** The name the run's log line and report carry; it rides `expireRequirementCompletions` on the job clock and has no hour of its own. */
export const RAISE_CERTIFICATION_TODOS_JOB = 'raiseCertificationToDos';


/** One `RequiredTraining` row as a person draws it or sends it. */
export interface RequiredTrainingDraft {
  requirementTypeId?: string | null;
  departmentId?: string | null;
  jobRoleId?: string | null;
}

/** 🔒 Exactly one of a department and a job role, and always a requirement; the database holds the first rule as well. */
export function requiredTrainingProblem(draft: RequiredTrainingDraft): string | null {
  if (!draft.requirementTypeId) return 'A required training row names the requirement it asks for.';
  const department = Boolean(draft.departmentId);
  const jobRole = Boolean(draft.jobRoleId);
  if (department && jobRole) return 'A required training row names a department or a job role, never both.';
  if (!department && !jobRole) return 'A required training row names a department or a job role.';
  return null;
}

/** One active employment at the Organization's own Company, with the position in force. */
export interface RegisterEmployment {
  employmentId: string;
  personId: string;
  name: string;
  departmentId: string | null;
  jobRoleId: string | null;
}

/** One `RequirementCompletion` row, as the register reads it. */
export interface RegisterCompletion {
  id: string;
  personId: string;
  requirementTypeId: string;
  status: string;
  expiresAt: Date | null;
}

/** One `RequiredTraining` row, as the register reads it. */
export interface RegisterRequirement {
  id: string;
  requirementTypeId: string;
  departmentId: string | null;
  jobRoleId: string | null;
  isActive: boolean;
}

/** One certification lapsing inside the lead time. */
export interface ExpiringCertificationRow {
  completionId: string;
  employmentId: string;
  personId: string;
  personName: string;
  requirementTypeId: string;
  requirementName: string;
  /** The day it lapses, as the ISO day of the stored instant. */
  expiresOn: string;
  /** Whole days from now to the lapse; nought or less is a lapse the nightly expiry has not yet moved. */
  daysLeft: number;
}

/** One requirement a `RequiredTraining` row asks of an employment and no current completion answers. */
export interface MissingCoverageRow {
  employmentId: string;
  personId: string;
  personName: string;
  requirementTypeId: string;
  requirementName: string;
  requiredBy: 'Department' | 'JobRole';
  requiredByName: string;
}

/** A completion that answers today: Current, and either never lapsing or lapsing after the moment asked. */
function inForce(row: RegisterCompletion, at: Date): boolean {
  return row.status === 'Current' && (row.expiresAt === null || row.expiresAt.getTime() > at.getTime());
}

/**
 * The person's latest standing completion of a type: Current, the one lapsing
 * last, and a completion that never lapses outranks every dated one. A renewal
 * is a newer Current row, so the older one stops counting the moment it lands.
 */
export function standingCompletion(completions: readonly RegisterCompletion[], personId: string, requirementTypeId: string): RegisterCompletion | null {
  let best: RegisterCompletion | null = null;
  for (const row of completions) {
    if (row.personId !== personId || row.requirementTypeId !== requirementTypeId || row.status !== 'Current') continue;
    if (best === null) best = row;
    else if (best.expiresAt !== null && (row.expiresAt === null || row.expiresAt.getTime() > best.expiresAt.getTime())) best = row;
  }
  return best;
}

/** 🔒 Expiring: a standing completion that lapses within `leadDays` of now, soonest first, each naming the person, the requirement and the day. */
export function expiringCertifications(
  employments: readonly RegisterEmployment[], completions: readonly RegisterCompletion[], requirementNames: ReadonlyMap<string, string>, now: Date, leadDays: number,
): ExpiringCertificationRow[] {
  const horizon = now.getTime() + leadDays * DAY_MS;
  const rows: ExpiringCertificationRow[] = [];
  for (const employment of employments) {
    const types = new Set(completions.filter((row) => row.personId === employment.personId).map((row) => row.requirementTypeId));
    for (const typeId of types) {
      const standing = standingCompletion(completions, employment.personId, typeId);
      if (!standing || standing.expiresAt === null || standing.expiresAt.getTime() > horizon) continue;
      rows.push({
        completionId: standing.id, employmentId: employment.employmentId, personId: employment.personId, personName: employment.name,
        requirementTypeId: typeId, requirementName: requirementNames.get(typeId) ?? typeId,
        expiresOn: standing.expiresAt.toISOString().slice(0, 10), daysLeft: Math.ceil((standing.expiresAt.getTime() - now.getTime()) / DAY_MS),
      });
    }
  }
  return rows.sort((a, b) => a.expiresOn.localeCompare(b.expiresOn) || a.personName.localeCompare(b.personName) || a.requirementName.localeCompare(b.requirementName));
}

/**
 * 🔒 Missing coverage: every active `RequiredTraining` row whose department or
 * job role an employment's position in force carries, and no completion in
 * force answers. One row per employment and requirement, however many rows
 * ask for it; a department's row is named before a job role's.
 */
export function missingCoverage(
  employments: readonly RegisterEmployment[], required: readonly RegisterRequirement[], completions: readonly RegisterCompletion[],
  names: { requirement: ReadonlyMap<string, string>; department: ReadonlyMap<string, string>; jobRole: ReadonlyMap<string, string> }, now: Date,
): MissingCoverageRow[] {
  const rows: MissingCoverageRow[] = [];
  const seen = new Set<string>();
  const ordered = [...required].filter((row) => row.isActive).sort((a, b) => Number(a.departmentId === null) - Number(b.departmentId === null));
  for (const employment of employments) {
    for (const row of ordered) {
      const byDepartment = row.departmentId !== null && row.departmentId === employment.departmentId;
      const byJobRole = row.jobRoleId !== null && row.jobRoleId === employment.jobRoleId;
      if (!byDepartment && !byJobRole) continue;
      const key = `${employment.employmentId}:${row.requirementTypeId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (completions.some((c) => c.personId === employment.personId && c.requirementTypeId === row.requirementTypeId && inForce(c, now))) continue;
      rows.push({
        employmentId: employment.employmentId, personId: employment.personId, personName: employment.name,
        requirementTypeId: row.requirementTypeId, requirementName: names.requirement.get(row.requirementTypeId) ?? row.requirementTypeId,
        requiredBy: byDepartment ? 'Department' : 'JobRole',
        requiredByName: byDepartment ? names.department.get(row.departmentId!) ?? '' : names.jobRole.get(row.jobRoleId!) ?? '',
      });
    }
  }
  return rows.sort((a, b) => a.personName.localeCompare(b.personName) || a.requirementName.localeCompare(b.requirementName));
}

/** One `RequiredTraining` row on the register's editor. */
export interface RequiredTrainingRow {
  id: string;
  requirementTypeId: string;
  requirementName: string;
  departmentId: string | null;
  departmentName: string | null;
  jobRoleId: string | null;
  jobRoleName: string | null;
  isActive: boolean;
}

/** What the register's route answers. */
export interface CertificationRegisterAnswer {
  leadDays: number;
  expiring: ExpiringCertificationRow[];
  missing: MissingCoverageRow[];
  required: RequiredTrainingRow[];
  /** 🔒 Whether this viewer may Add Requirement and Deactivate (decision 3182): `hr.hire`, more than the permission to view HR. */
  editsRequired?: boolean;
  options: {
    requirementTypes: { id: string; name: string }[];
    departments: { id: string; name: string }[];
    jobRoles: { id: string; name: string }[];
  };
}
